import React from 'react'
import type { HandRecord, PlayerRecord, ActionRecord } from '@range-reader/shared'
import { PlayerTooltip } from '../players/PlayerTooltip'

// Position order: SB first, clockwise around the table
const POSITION_ORDER = ['SB', 'BB', 'UTG', 'UTG+1', 'MP', 'MP+1', 'HJ', 'CO', 'BTN']

// Place seats evenly around a horizontal oval.
// Angle 0 = bottom-center (hero), going clockwise.
// Returns pixel offsets from the center of a 600×320 reference frame.
const TABLE_W = 600
const TABLE_H = 320
const OVAL_RX = 210   // oval x-radius (seats placed on a slightly larger ellipse)
const OVAL_RY = 128   // oval y-radius

function getSeatPos(i: number, count: number): { x: number; y: number } {
  const angleDeg = (i * 360) / count
  const rad = (angleDeg * Math.PI) / 180
  return {
    x: TABLE_W / 2 - OVAL_RX * Math.sin(rad),
    y: TABLE_H / 2 + OVAL_RY * Math.cos(rad),
  }
}

// ── Card rendering ──────────────────────────────────────────────────
const SUIT_SYM: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' }
const RED = new Set(['h', 'd'])

function CardBack() {
  return <div className="poker-card-back" />
}

function CardFace({ card, large = false }: { card: string; large?: boolean }) {
  const rank = card.slice(0, -1)
  const suit = card.slice(-1)
  const colorCls = RED.has(suit) ? 'poker-card-red' : 'poker-card-black'
  return (
    <div className={`poker-card-face ${large ? 'large' : 'small'}`}>
      <span className={`poker-card-rank ${colorCls}`}>{rank}</span>
      <span className={`poker-card-suit ${colorCls}`}>{SUIT_SYM[suit] ?? suit}</span>
    </div>
  )
}

// ── Action badge ────────────────────────────────────────────────────
const ACTION_BG: Record<string, { bg: string; color: string }> = {
  fold:   { bg: '#4b5563', color: '#d1d5db' },
  check:  { bg: '#374151', color: '#fff' },
  call:   { bg: '#1d4ed8', color: '#fff' },
  bet:    { bg: '#c2410c', color: '#fff' },
  raise:  { bg: '#ea580c', color: '#fff' },
  allin:  { bg: '#dc2626', color: '#fff' },
}

function fmtAction(a: ActionRecord, bb: number): string {
  const bbAmt = bb > 0 ? bb : 1
  const amt = a.amount > 0 ? ` ${(a.amount / 100 / bbAmt).toFixed(1)}bb` : ''
  switch (a.action) {
    case 'fold':  return 'Fold'
    case 'check': return 'Check'
    case 'call':  return `Call${amt}`
    case 'bet':   return `Bet${amt}`
    case 'raise': return `Raise${amt}`
    case 'allin': return `All-In${amt}`
    default:      return a.action
  }
}

// ── Single seat ─────────────────────────────────────────────────────
interface SeatProps {
  player: PlayerRecord
  isTarget: boolean
  isFolded: boolean
  isAnonymized: boolean
  lastAction?: ActionRecord
  showRevealedCards: boolean
  holeCards?: string
  bigBlind: number
}

function Seat({ player, isTarget, isFolded, isAnonymized, lastAction, showRevealedCards, holeCards, bigBlind }: SeatProps) {
  const isNumericName = /^\d+$/.test(player.name.trim())
  const displayLabel = player.position ?? (isAnonymized ? `Seat` : (player.name.slice(0, 10)))
  const showRealName = !isAnonymized && !isNumericName && player.name && player.position
  const bb = bigBlind > 0 ? bigBlind / 100 : 1
  const stack = (player.stack / 100 / bb).toFixed(0)
  const stillIn = !isFolded
  const ab = lastAction ? (ACTION_BG[lastAction.action] ?? { bg: '#374151', color: '#fff' }) : null

  return (
    <div className={`poker-seat${isFolded ? ' folded' : ''}`}>
      {/* Face-down cards for active players */}
      <div className="poker-seat-cards">
        {stillIn && (
          showRevealedCards && isTarget && holeCards
            ? holeCards.split(' ').map((c, i) => <CardFace key={i} card={c} />)
            : [0, 1].map(i => <CardBack key={i} />)
        )}
      </div>

      {/* Action badge — dynamic colors kept inline since they vary per action type */}
      {lastAction && ab && (
        <div className="poker-action-badge" style={{ background: ab.bg, color: ab.color }}>
          {fmtAction(lastAction, bb)}
        </div>
      )}

      {/* Name chip */}
      <PlayerTooltip name={player.name}>
        <div className={`poker-name-chip ${isTarget ? 'target' : 'default'}`}>
          {isTarget ? '🎯 ' : ''}{displayLabel}
        </div>
      </PlayerTooltip>

      {/* Real name (if non-numeric and not anonymized) */}
      {showRealName && (
        <div className="poker-real-name">
          {player.name.length > 10 ? player.name.slice(0, 9) + '…' : player.name}
        </div>
      )}

      {/* Stack */}
      <div className="poker-stack">{stack}bb</div>
    </div>
  )
}

// ── Sort players into position order ────────────────────────────────
function sortByPosition(players: PlayerRecord[]): PlayerRecord[] {
  return [...players].sort((a, b) => {
    const ai = POSITION_ORDER.indexOf((a.position ?? '').toUpperCase())
    const bi = POSITION_ORDER.indexOf((b.position ?? '').toUpperCase())
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })
}

// ── Main table ───────────────────────────────────────────────────────
interface PokerTableProps {
  hand: HandRecord
  actionIndex: number
  isAnonymized: boolean
  showdownRevealed: boolean
}

export function PokerTable({ hand, actionIndex, isAnonymized, showdownRevealed }: PokerTableProps) {
  const { players, actions, communityCards, winner, bigBlind } = hand
  const targetSeat = winner.seat
  const bb = bigBlind > 0 ? bigBlind / 100 : 1

  const sorted = sortByPosition(players)
  const count = sorted.length

  // Process actions
  const foldedNames = new Set<string>()
  const lastByName: Record<string, ActionRecord> = {}
  let currentStreet = 'preflop'

  for (let i = 0; i <= actionIndex && i < actions.length; i++) {
    const a = actions[i]
    if (a.action === 'fold') foldedNames.add(a.player)
    lastByName[a.player] = a
    currentStreet = a.street
  }

  // Community cards
  const allCards = (communityCards ?? '').split(' ').filter(Boolean)
  let visible: string[] = []
  if (currentStreet === 'flop')              visible = allCards.slice(0, 3)
  else if (currentStreet === 'turn')         visible = allCards.slice(0, 4)
  else if (currentStreet === 'river' || showdownRevealed) visible = allCards

  // Pot
  let potCents = Math.round(bigBlind * 1.5)
  for (let i = 0; i <= actionIndex && i < actions.length; i++) {
    const a = actions[i]
    if (['bet', 'raise', 'call', 'allin'].includes(a.action)) potCents += a.amount
  }

  // Seat positions on the reference frame
  const positions = sorted.map((_, i) => getSeatPos(i, count))

  const refW = TABLE_W + 160
  const refH = TABLE_H + 160

  return (
    <div className="poker-table-wrapper">
      {/* Fixed-size reference container that scales to fit */}
      <div className="poker-table-ref" style={{ width: refW, height: refH }}>
        {/* ── Felt oval (centered in reference frame) ── */}
        <div className="poker-felt" style={{ width: TABLE_W, height: TABLE_H }}>
          {/* Inner rail line */}
          <div className="poker-felt-rail" />

          {/* Community cards + pot — centered on felt */}
          <div className="poker-center-area">
            {visible.length > 0 ? (
              <div className="poker-community-cards">
                {visible.map((c, i) => <CardFace key={i} card={c} large />)}
              </div>
            ) : (
              <span className="poker-preflop-label">
                {currentStreet === 'preflop' ? 'Preflop' : ''}
              </span>
            )}
            {potCents > 0 && (
              <div className="poker-pot-badge">
                <span className="poker-pot-icon">●</span>
                Pot: {(potCents / 100 / bb).toFixed(1)}bb
              </div>
            )}
          </div>
        </div>

        {/* ── Seats absolutely positioned around the oval ── */}
        {sorted.map((player, i) => {
          const { x, y } = positions[i]
          const px = x + 80
          const py = y + 80
          return (
            <div key={player.seat} className="poker-seat-pos" style={{ left: px, top: py }}>
              <Seat
                player={player}
                isTarget={player.seat === targetSeat}
                isFolded={foldedNames.has(player.name)}
                isAnonymized={isAnonymized}
                lastAction={lastByName[player.name]}
                showRevealedCards={showdownRevealed}
                holeCards={player.seat === targetSeat ? hand.winner.holeCards : undefined}
                bigBlind={bigBlind}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
